import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import './UserProfile.css';
import VerificationModal from './VerificationModal';
import {
  FaUser,
  FaEdit,
  FaSave,
  FaTimes,
  FaLock,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaUserTie,
  FaUserShield,
  FaCrown,
  FaCalendarAlt,
  FaBell,
  FaGlobe,
  FaClock,
  FaBuilding,
  FaIdCard,
  FaUsers,
  FaKey,
  FaEye,
  FaEyeSlash
} from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export default function UserProfile({ userId = null, isModal = false, onClose = null }) {
  const { token, role: currentUserRole } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Email change state
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [emailCodeVerified, setEmailCodeVerified] = useState(false);
  const [sendingEmailCode, setSendingEmailCode] = useState(false);
  const [verifyingEmailCode, setVerifyingEmailCode] = useState(false);
  const [emailCodeSecondsLeft, setEmailCodeSecondsLeft] = useState(0);
  const emailTimerRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    profile: {
      phone: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA'
      },
      emergencyContact: {
        name: '',
        phone: '',
        relationship: ''
      },
      preferences: {
        notifications: {
          email: true,
          sms: false
        },
        language: 'en',
        timezone: 'Asia/Manila'
      }
    },
    clientData: {
      membershipType: 'basic',
      preferredPaymentMethod: ''
    },
    staffData: {
      employeeId: '',
      department: '',
      position: '',
      schedule: {
        workDays: [],
        startTime: '09:00',
        endTime: '17:00'
      }
    },
    adminData: {
      adminLevel: 'supervisor',
      departments: []
    }
  });

  // Load profile data
  useEffect(() => {
    loadProfile();
  }, [userId]);

  // Timer for email verification resend
  useEffect(() => {
    if (emailCodeSecondsLeft > 0) {
      emailTimerRef.current = setTimeout(() => {
        setEmailCodeSecondsLeft(emailCodeSecondsLeft - 1);
      }, 1000);
    }
    return () => {
      if (emailTimerRef.current) clearTimeout(emailTimerRef.current);
    };
  }, [emailCodeSecondsLeft]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const endpoint = userId ? `/profile/${userId}` : '/profile';
      const res = await axios.get(`${API_BASE}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProfile(res.data);
      setFormData({
        firstName: res.data.firstName || '',
        lastName: res.data.lastName || '',
        profile: {
          phone: res.data.profile?.phone || '',
          address: {
            street: res.data.profile?.address?.street || '',
            city: res.data.profile?.address?.city || '',
            state: res.data.profile?.address?.state || '',
            zipCode: res.data.profile?.address?.zipCode || '',
            country: res.data.profile?.address?.country || 'USA'
          },
          emergencyContact: {
            name: res.data.profile?.emergencyContact?.name || '',
            phone: res.data.profile?.emergencyContact?.phone || '',
            relationship: res.data.profile?.emergencyContact?.relationship || ''
          },
          preferences: {
            notifications: {
              email: res.data.profile?.preferences?.notifications?.email !== false,
              sms: res.data.profile?.preferences?.notifications?.sms || false
            },
            language: res.data.profile?.preferences?.language || 'en',
            timezone: res.data.profile?.preferences?.timezone || 'Asia/Manila'
          }
        },
        clientData: {
          membershipType: res.data.clientData?.membershipType || 'basic',
          preferredPaymentMethod: res.data.clientData?.preferredPaymentMethod || ''
        },
        staffData: {
          employeeId: res.data.staffData?.employeeId || '',
          department: res.data.staffData?.department || '',
          position: res.data.staffData?.position || '',
          schedule: {
            workDays: res.data.staffData?.schedule?.workDays || [],
            startTime: res.data.staffData?.schedule?.startTime || '09:00',
            endTime: res.data.staffData?.schedule?.endTime || '17:00'
          }
        },
        adminData: {
          adminLevel: res.data.adminData?.adminLevel || 'supervisor',
          departments: res.data.adminData?.departments || []
        }
      });
      setError('');
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to load profile');
      console.error('Load profile error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setError('');
    try {
      const endpoint = userId && currentUserRole === 'admin' ? `/profile/${userId}` : '/profile';
      await axios.put(`${API_BASE}${endpoint}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Profile updated successfully!');
      setEditing(false);
      await loadProfile();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.msg || err.response?.data?.message || err.message || 'Failed to update profile';
      setError(errorMessage);
      console.error('Save profile error:', err);
      console.error('Error response:', err.response?.data);
      
      // Keep error message visible
      setTimeout(() => setError(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    // Strong password validation (same as registration)
    const hasMinLength = passwordData.newPassword.length >= 8;
    const hasMaxLength = passwordData.newPassword.length <= 20;
    const hasUppercase = /[A-Z]/.test(passwordData.newPassword);
    const hasLowercase = /[a-z]/.test(passwordData.newPassword);
    const hasNumbers = /\d/.test(passwordData.newPassword);
    const hasSymbols = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(passwordData.newPassword);
    
    if (!hasMinLength) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (!hasMaxLength) {
      setError('Password must not exceed 20 characters');
      return;
    }
    
    if (!hasUppercase || !hasLowercase || !hasNumbers || !hasSymbols) {
      const missing = [];
      if (!hasUppercase) missing.push('uppercase letter');
      if (!hasLowercase) missing.push('lowercase letter');
      if (!hasNumbers) missing.push('number');
      if (!hasSymbols) missing.push('special character');
      setError(`Password needs: ${missing.join(', ')}`);
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API_BASE}/profile/password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Password changed successfully!');
      setShowPasswordChange(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to change password');
      console.error('Change password error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleRequestEmailChange = async () => {
    setEmailError('');
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setEmailError('Invalid email format');
      return;
    }

    if (newEmail === profile.email) {
      setEmailError('New email must be different from current email');
      return;
    }

    setSendingEmailCode(true);
    try {
      await axios.post(`${API_BASE}/profile/request-email-change`, {
        newEmail
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowEmailVerificationModal(true);
      setEmailCodeSecondsLeft(60);
      setEmailError('');
    } catch (err) {
      setEmailError(err.response?.data?.msg || 'Failed to send verification code');
      console.error('Request email change error:', err);
    } finally {
      setSendingEmailCode(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    setEmailError('');
    setVerifyingEmailCode(true);
    
    try {
      const res = await axios.post(`${API_BASE}/profile/verify-email-change`, {
        code: emailVerificationCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEmailCodeVerified(true);
      setSuccess('Email updated successfully!');
      setShowEmailVerificationModal(false);
      setShowEmailChange(false);
      setNewEmail('');
      setEmailVerificationCode('');
      
      // Reload profile to get updated email
      await loadProfile();
      
      setTimeout(() => {
        setSuccess('');
        setEmailCodeVerified(false);
      }, 3000);
    } catch (err) {
      setEmailError(err.response?.data?.msg || 'Invalid verification code');
      console.error('Verify email code error:', err);
    } finally {
      setVerifyingEmailCode(false);
    }
  };

  const handleResendEmailCode = async () => {
    setEmailError('');
    setSendingEmailCode(true);
    
    try {
      await axios.post(`${API_BASE}/profile/request-email-change`, {
        newEmail
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEmailCodeSecondsLeft(60);
      setEmailError('');
    } catch (err) {
      setEmailError(err.response?.data?.msg || 'Failed to resend verification code');
      console.error('Resend email code error:', err);
    } finally {
      setSendingEmailCode(false);
    }
  };

  const handleInputChange = (path, value) => {
    setFormData(prev => {
      const pathArray = path.split('.');
      
      // For top-level fields
      if (pathArray.length === 1) {
        return { ...prev, [path]: value };
      }
      
      // For nested fields - create a shallow copy chain
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < pathArray.length - 1; i++) {
        current[pathArray[i]] = { ...current[pathArray[i]] };
        current = current[pathArray[i]];
      }
      
      current[pathArray[pathArray.length - 1]] = value;
      return newData;
    });
  };
  
  // Separate handler for numeric-only fields to prevent cursor jump  
  const handleNumericChange = (path, e) => {
    const inputValue = e.target.value;
    const numericValue = inputValue.replace(/[^0-9]/g, '');
    
    setFormData(prevData => {
      const keys = path.split('.');
      
      // Use shallow copy instead of deep clone
      const newData = { ...prevData };
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = numericValue;
      return newData;
    });
  };

  const getRoleIcon = (userRole) => {
    switch (userRole) {
      case 'admin': return <FaUserShield className="role-icon admin" />;
      case 'staff': return <FaUserTie className="role-icon staff" />;
      case 'client': return <FaUser className="role-icon client" />;
      default: return <FaUser className="role-icon" />;
    }
  };

  const getRoleBadge = (userRole) => {
    const badges = {
      admin: { class: 'admin', icon: <FaCrown />, text: 'Administrator' },
      staff: { class: 'staff', icon: <FaUserTie />, text: 'Staff Member' },
      client: { class: 'client', icon: <FaUser />, text: 'Client' }
    };
    return badges[userRole] || badges.client;
  };

  if (loading) {
    return (
      <div className={`profile-container ${isModal ? 'modal' : ''}`}>
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={`profile-container ${isModal ? 'modal' : ''}`}>
        <div className="error-message">
          <p>Profile not found</p>
          {isModal && (
            <button onClick={onClose} className="btn-secondary">Close</button>
          )}
        </div>
      </div>
    );
  }

  const badge = getRoleBadge(profile.role);
  const canEdit = !userId || (userId && currentUserRole === 'admin');
  const isOwnProfile = !userId;

  const profileContent = (
    <>
        {/* Profile Header */}
        <div className="profile-header">
          <div className="profile-avatar">
            {profile.profile?.avatar ? (
              <img src={profile.profile.avatar} alt="Profile" />
            ) : (
              <div className="avatar-placeholder">
                {getRoleIcon(profile.role)}
              </div>
            )}
          </div>
          
          <div className="profile-info">
            <h1 className="profile-name">
              {profile.fullName || `${profile.firstName} ${profile.lastName}`.trim() || 'User'}
            </h1>
            <div className={`role-badge ${badge.class}`}>
              {badge.icon}
              <span>{badge.text}</span>
            </div>
            <p className="profile-email">
              <FaEnvelope /> {profile.email}
            </p>
            {profile.profile?.phone && (
              <p className="profile-phone">
                <FaPhone /> {profile.profile.phone}
              </p>
            )}
          </div>

          <div className="profile-actions">
            {canEdit && (
              <>
                {!editing ? (
                  <button 
                    onClick={() => setEditing(true)} 
                    className="btn-primary"
                    disabled={saving}
                  >
                    <FaEdit /> Edit Profile
                  </button>
                ) : (
                  <div className="edit-actions">
                    <button 
                      onClick={handleSaveProfile} 
                      className="btn-success"
                      disabled={saving}
                    >
                      <FaSave /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button 
                      onClick={() => {
                        setEditing(false);
                        loadProfile();
                      }} 
                      className="btn-secondary"
                      disabled={saving}
                    >
                      <FaTimes /> Cancel
                    </button>
                  </div>
                )}
                
                {isOwnProfile && (
                  <button 
                    onClick={() => setShowPasswordChange(!showPasswordChange)} 
                    className="btn-outline"
                  >
                    <FaLock /> Change Password
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="alert alert-error">
            {error}
            <button onClick={() => setError('')} className="alert-close">×</button>
          </div>
        )}
        
        {success && (
          <div className="alert alert-success">
            {success}
            <button onClick={() => setSuccess('')} className="alert-close">×</button>
          </div>
        )}

        {/* Password Change Section */}
        {showPasswordChange && isOwnProfile && (
          <div className="password-change-section">
            <h3><FaLock /> Change Password</h3>
            <div className="password-form">
              <div className="form-group">
                <label>Current Password</label>
                <div className="password-input">
                  <input
                    type={showPassword.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={e => setPasswordData(prev => ({
                      ...prev,
                      currentPassword: e.target.value
                    }))}
                    placeholder="Enter current password"
                    maxLength={20}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => ({
                      ...prev,
                      current: !prev.current
                    }))}
                    className="password-toggle"
                  >
                    {showPassword.current ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
              
              <div className="form-group">
                <label>New Password</label>
                <div className="password-input">
                  <input
                    type={showPassword.new ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={e => setPasswordData(prev => ({
                      ...prev,
                      newPassword: e.target.value
                    }))}
                    placeholder="Enter new password (8-20 chars)"
                    maxLength={20}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => ({
                      ...prev,
                      new: !prev.new
                    }))}
                    className="password-toggle"
                  >
                    {showPassword.new ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
              
              <div className="form-group">
                <label>Confirm New Password</label>
                <div className="password-input">
                  <input
                    type={showPassword.confirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={e => setPasswordData(prev => ({
                      ...prev,
                      confirmPassword: e.target.value
                    }))}
                    placeholder="Confirm new password"
                    maxLength={20}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => ({
                      ...prev,
                      confirm: !prev.confirm
                    }))}
                    className="password-toggle"
                  >
                    {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
              
              <div className="password-actions">
                <button 
                  onClick={handleChangePassword} 
                  className="btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Changing...' : 'Change Password'}
                </button>
                <button 
                  onClick={() => {
                    setShowPasswordChange(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }} 
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Email Change Section */}
        {showEmailChange && isOwnProfile && (
          <div className="password-change-section">
            <h3><FaEnvelope /> Change Email Address</h3>
            <div className="password-form">
              <div className="form-group">
                <label>Current Email</label>
                <span className="form-value">{profile.email}</span>
              </div>
              
              <div className="form-group">
                <label>New Email</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder="Enter new email address"
                  maxLength={30}
                />
                {emailError && <small className="error" style={{ display: 'block', marginTop: '4px' }}>{emailError}</small>}
              </div>
              
              <div className="password-actions">
                <button 
                  onClick={handleRequestEmailChange} 
                  className="btn-primary"
                  disabled={sendingEmailCode || !newEmail}
                >
                  {sendingEmailCode ? 'Sending Code...' : 'Send Verification Code'}
                </button>
                <button 
                  onClick={() => {
                    setShowEmailChange(false);
                    setNewEmail('');
                    setEmailError('');
                  }} 
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Email Verification Modal */}
        <VerificationModal
          show={showEmailVerificationModal}
          onClose={() => {
            setShowEmailVerificationModal(false);
            setEmailVerificationCode('');
            setEmailError('');
          }}
          code={emailVerificationCode}
          onCodeChange={setEmailVerificationCode}
          onVerify={handleVerifyEmailCode}
          onResend={handleResendEmailCode}
          secondsLeft={emailCodeSecondsLeft}
          verifying={verifyingEmailCode}
          verified={emailCodeVerified}
          error={emailError}
        />

        {/* Profile Sections */}
        <div className="profile-sections">
          {/* Basic Information */}
          <div className="profile-section">
            <h3><FaUser /> Basic Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>First Name</label>
                {editing ? (
                  <input
                    key="firstName-input"
                    type="text"
                    value={formData.firstName}
                    onChange={e => handleInputChange('firstName', e.target.value)}
                    placeholder="First Name"
                    maxLength={20}
                    pattern="[a-zA-Z\s\-']+"
                    title="First name can only contain letters, spaces, hyphens, and apostrophes"
                    autoComplete="off"
                  />
                ) : (
                  <span className="form-value">{profile.firstName || 'Not set'}</span>
                )}
              </div>
              
              <div className="form-group">
                <label>Last Name</label>
                {editing ? (
                  <input
                    key="lastName-input"
                    type="text"
                    value={formData.lastName}
                    onChange={e => handleInputChange('lastName', e.target.value)}
                    placeholder="Last Name"
                    maxLength={20}
                    pattern="[a-zA-Z\s\-']+"
                    title="Last name can only contain letters, spaces, hyphens, and apostrophes"
                    autoComplete="off"
                  />
                ) : (
                  <span className="form-value">{profile.lastName || 'Not set'}</span>
                )}
              </div>
              
              <div className="form-group">
                <label>Email</label>
                {editing && isOwnProfile && profile.role === 'client' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span className="form-value">{profile.email}</span>
                      <button
                        type="button"
                        onClick={() => setShowEmailChange(true)}
                        className="btn-outline"
                        style={{ 
                          padding: '4px 12px', 
                          fontSize: '12px',
                          height: 'auto'
                        }}
                      >
                        Change Email
                      </button>
                    </div>
                  </div>
                ) : (
                  <span className="form-value">{profile.email}</span>
                )}
              </div>
              
              <div className="form-group">
                <label>Phone</label>
                {editing ? (
                  <div key="phone-wrapper">
                    <input
                      type="tel"
                      value={formData.profile.phone}
                      onChange={e => handleNumericChange('profile.phone', e)}
                      placeholder="09171234567"
                      maxLength={11}
                      pattern="[0-9]{11}"
                      title="Phone number must be exactly 11 digits (e.g., 09171234567)"
                      autoComplete="off"
                    />
                    <small style={{ color: '#6c757d', fontSize: '11px', fontStyle: 'italic', display: 'block', marginTop: '4px' }}>
                      Exactly 11 digits (e.g., 09171234567)
                    </small>
                  </div>
                ) : (
                  <span className="form-value">{profile.profile?.phone || 'Not set'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="profile-section">
            <h3><FaMapMarkerAlt /> Address</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Street Address</label>
                {editing ? (
                  <input
                    key="street-input"
                    type="text"
                    value={formData.profile.address.street}
                    onChange={e => handleInputChange('profile.address.street', e.target.value)}
                    placeholder="Street Address"
                    autoComplete="off"
                  />
                ) : (
                  <span className="form-value">{profile.profile?.address?.street || 'Not set'}</span>
                )}
              </div>
              
              <div className="form-group">
                <label>City</label>
                {editing ? (
                  <input
                    key="city-input"
                    type="text"
                    value={formData.profile.address.city}
                    onChange={e => handleInputChange('profile.address.city', e.target.value)}
                    placeholder="City"
                    autoComplete="off"
                  />
                ) : (
                  <span className="form-value">{profile.profile?.address?.city || 'Not set'}</span>
                )}
              </div>
              
              <div className="form-group">
                <label>State</label>
                {editing ? (
                  <input
                    key="state-input"
                    type="text"
                    value={formData.profile.address.state}
                    onChange={e => handleInputChange('profile.address.state', e.target.value)}
                    placeholder="State"
                    autoComplete="off"
                  />
                ) : (
                  <span className="form-value">{profile.profile?.address?.state || 'Not set'}</span>
                )}
              </div>
              
              <div className="form-group">
                <label>ZIP Code</label>
                {editing ? (
                  <div key="zipCode-wrapper">
                    <input
                      type="text"
                      value={formData.profile.address.zipCode}
                      onChange={e => handleNumericChange('profile.address.zipCode', e)}
                      placeholder="ZIP Code"
                      maxLength={10}
                      pattern="[0-9]+"
                      title="ZIP code can only contain numbers"
                      autoComplete="off"
                    />
                    <small style={{ color: '#6c757d', fontSize: '11px', fontStyle: 'italic', display: 'block', marginTop: '4px' }}>
                      Numbers only
                    </small>
                  </div>
                ) : (
                  <span className="form-value">{profile.profile?.address?.zipCode || 'Not set'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="profile-section">
            <h3><FaUsers /> Emergency Contact</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Name</label>
                {editing ? (
                  <input
                    key="emergency-name-input"
                    type="text"
                    value={formData.profile.emergencyContact.name}
                    onChange={e => handleInputChange('profile.emergencyContact.name', e.target.value)}
                    placeholder="Emergency Contact Name"
                    maxLength={20}
                    pattern="[a-zA-Z\s\-']+"
                    title="Name can only contain letters, spaces, hyphens, and apostrophes"
                    autoComplete="off"
                  />
                ) : (
                  <span className="form-value">{profile.profile?.emergencyContact?.name || 'Not set'}</span>
                )}
              </div>
              
              <div className="form-group">
                <label>Phone</label>
                {editing ? (
                  <div key="emergency-phone-wrapper">
                    <input
                      type="tel"
                      value={formData.profile.emergencyContact.phone}
                      onChange={e => handleNumericChange('profile.emergencyContact.phone', e)}
                      placeholder="09171234567"
                      maxLength={11}
                      pattern="[0-9]{11}"
                      title="Phone number must be exactly 11 digits (e.g., 09171234567)"
                      autoComplete="off"
                    />
                    <small style={{ color: '#6c757d', fontSize: '11px', fontStyle: 'italic', display: 'block', marginTop: '4px' }}>
                      Exactly 11 digits (e.g., 09171234567)
                    </small>
                  </div>
                ) : (
                  <span className="form-value">{profile.profile?.emergencyContact?.phone || 'Not set'}</span>
                )}
              </div>
              
              <div className="form-group">
                <label>Relationship</label>
                {editing ? (
                  <input
                    key="relationship-input"
                    type="text"
                    value={formData.profile.emergencyContact.relationship}
                    onChange={e => handleInputChange('profile.emergencyContact.relationship', e.target.value)}
                    placeholder="Relationship"
                    maxLength={30}
                    autoComplete="off"
                  />
                ) : (
                  <span className="form-value">{profile.profile?.emergencyContact?.relationship || 'Not set'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Preferences */}
          <div className="profile-section">
            <h3><FaBell /> Preferences</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Email Notifications</label>
                {editing ? (
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      checked={formData.profile.preferences.notifications.email}
                      onChange={e => handleInputChange('profile.preferences.notifications.email', e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    Enable email notifications
                  </label>
                ) : (
                  <span className="form-value">
                    {profile.profile?.preferences?.notifications?.email ? 'Enabled' : 'Disabled'}
                  </span>
                )}
              </div>
              
              <div className="form-group">
                <label>SMS Notifications</label>
                {editing ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label className="checkbox-container" style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                      <input
                        type="checkbox"
                        checked={false}
                        disabled={true}
                      />
                      <span className="checkmark"></span>
                      Enable SMS notifications
                    </label>
                    <small style={{ color: '#6c757d', fontSize: '12px', fontStyle: 'italic' }}>
                      Coming soon - Future implementation
                    </small>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span className="form-value">Disabled</span>
                    <small style={{ color: '#6c757d', fontSize: '12px', fontStyle: 'italic' }}>
                      Coming soon
                    </small>
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label>Language</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span className="form-value">English</span>
                  <small style={{ color: '#6c757d', fontSize: '12px', fontStyle: 'italic' }}>
                    Default language
                  </small>
                </div>
              </div>
              
              <div className="form-group">
                <label>Timezone</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span className="form-value">Philippine Time (PHT)</span>
                  <small style={{ color: '#6c757d', fontSize: '12px', fontStyle: 'italic' }}>
                    Default timezone for all users
                  </small>
                </div>
              </div>
            </div>
          </div>

          {/* Role-specific sections */}
          {profile.role === 'client' && (
            <div className="profile-section">
              <h3><FaUser /> Client Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Membership Type</label>
                  <span className="form-value">Client</span>
                </div>
                
                <div className="form-group">
                  <label>Member Since</label>
                  <span className="form-value">
                    {profile.clientData?.memberSince ? 
                      new Date(profile.clientData.memberSince).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
                
                <div className="form-group">
                  <label>Total Reservations</label>
                  <span className="form-value">
                    {profile.clientData?.totalReservations || 0}
                  </span>
                </div>
                
                <div className="form-group">
                  <label>Preferred Payment Method</label>
                  {editing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <select
                        value={formData.clientData.preferredPaymentMethod}
                        onChange={e => handleInputChange('clientData.preferredPaymentMethod', e.target.value)}
                      >
                        <option value="">Select preferred method</option>
                        <option value="gcash">GCash</option>
                        <option value="bank_transfer">Bank Transfer</option>
                      </select>
                      <small style={{ color: '#6c757d', fontSize: '12px', fontStyle: 'italic' }}>
                        This will be pre-selected in booking forms
                      </small>
                    </div>
                  ) : (
                    <span className="form-value">
                      {profile.clientData?.preferredPaymentMethod === 'gcash' ? 'GCash' :
                       profile.clientData?.preferredPaymentMethod === 'bank_transfer' ? 'Bank Transfer' :
                       'Not set'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {profile.role === 'staff' && (
            <div className="profile-section">
              <h3><FaUserTie /> Staff Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Employee ID</label>
                  {editing && currentUserRole === 'admin' ? (
                    <input
                      type="text"
                      value={formData.staffData.employeeId}
                      onChange={e => handleInputChange('staffData.employeeId', e.target.value)}
                      placeholder="Employee ID"
                    />
                  ) : (
                    <span className="form-value">
                      {profile.staffData?.employeeId || 'Not set'}
                    </span>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Department</label>
                  {editing ? (
                    <input
                      type="text"
                      value={formData.staffData.department}
                      onChange={e => handleInputChange('staffData.department', e.target.value)}
                      placeholder="Department"
                    />
                  ) : (
                    <span className="form-value">
                      {profile.staffData?.department || 'Not set'}
                    </span>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Position</label>
                  {editing ? (
                    <input
                      type="text"
                      value={formData.staffData.position}
                      onChange={e => handleInputChange('staffData.position', e.target.value)}
                      placeholder="Position"
                    />
                  ) : (
                    <span className="form-value">
                      {profile.staffData?.position || 'Not set'}
                    </span>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Hire Date</label>
                  <span className="form-value">
                    {profile.staffData?.hireDate ? 
                      new Date(profile.staffData.hireDate).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
                
                <div className="form-group">
                  <label>Supervisor</label>
                  <span className="form-value">
                    {profile.staffData?.supervisor ? 
                      `${profile.staffData.supervisor.firstName} ${profile.staffData.supervisor.lastName}` : 'Not assigned'}
                  </span>
                </div>
                
                <div className="form-group full-width">
                  <label>Work Schedule</label>
                  <div className="schedule-display">
                    <p><strong>Work Days:</strong> {profile.staffData?.schedule?.workDays?.join(', ') || 'Not set'}</p>
                    <p><strong>Hours:</strong> {profile.staffData?.schedule?.startTime || '09:00'} - {profile.staffData?.schedule?.endTime || '17:00'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {profile.role === 'admin' && (
            <div className="profile-section">
              <h3><FaUserShield /> Administrator Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Admin Level</label>
                  {editing && currentUserRole === 'admin' ? (
                    <select
                      value={formData.adminData.adminLevel}
                      onChange={e => handleInputChange('adminData.adminLevel', e.target.value)}
                    >
                      <option value="supervisor">Supervisor</option>
                      <option value="manager">Manager</option>
                      <option value="super">Super Admin</option>
                    </select>
                  ) : (
                    <span className="form-value capitalize">
                      {profile.adminData?.adminLevel || 'Supervisor'}
                    </span>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Departments Overseen</label>
                  <span className="form-value">
                    {profile.adminData?.departments?.length ? 
                      profile.adminData.departments.join(', ') : 'None'}
                  </span>
                </div>
                
                <div className="form-group">
                  <label>Last System Access</label>
                  <span className="form-value">
                    {profile.adminData?.lastSystemAccess ? 
                      new Date(profile.adminData.lastSystemAccess).toLocaleString() : 'Never'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Account Status */}
          <div className="profile-section">
            <h3><FaCalendarAlt /> Account Status</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Account Created</label>
                <span className="form-value">
                  {profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              
              <div className="form-group">
                <label>Last Login</label>
                <span className="form-value">
                  {profile.lastLogin ? new Date(profile.lastLogin).toLocaleString() : 'Never'}
                </span>
              </div>
              
              <div className="form-group">
                <label>Email Verified</label>
                <span className={`form-value status ${profile.emailVerified ? 'verified' : 'unverified'}`}>
                  {profile.emailVerified ? 'Verified' : 'Not Verified'}
                </span>
              </div>
              
              <div className="form-group">
                <label>Account Status</label>
                <span className={`form-value status ${profile.isActive ? 'active' : 'inactive'}`}>
                  {profile.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </>
  );

  return (
    <div className={`profile-container ${isModal ? 'modal' : ''}`}>
      {isModal && (
        <div className="modal-backdrop" onClick={onClose}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={onClose}>
              <FaTimes />
            </button>
            {profileContent}
          </div>
        </div>
      )}
      
      {!isModal && profileContent}
    </div>
  );
} 